import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';

const emptyTier = { name: '', emails: '', priceRands: '', sortOrder: '0' };

function EmailTopupTiers() {
  const [tiers, setTiers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState({});
  const [newTier, setNewTier] = useState(emptyTier);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [tierRes, purchaseRes] = await Promise.all([
        axios.get('/api/admin/email-topup-tiers'),
        axios.get('/api/admin/email-topup-purchases')
      ]);
      setTiers(tierRes.data);
      setPurchases(purchaseRes.data);
      // Seed the edit fields from what is stored.
      const seeded = {};
      tierRes.data.forEach(t => {
        seeded[t.id] = {
          name: t.name,
          emails: String(t.emails),
          priceRands: (t.price_cents / 100).toFixed(2),
          sortOrder: String(t.sort_order ?? 0),
          isActive: t.is_active
        };
      });
      setEditing(seeded);
    } catch (err) {
      console.error('Error loading top-up tiers:', err);
      setError(err.response?.data?.error || 'Error loading tiers');
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (id, field, value) => {
    setEditing(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveTier = async (id) => {
    const tier = editing[id];
    setBusy(true);
    try {
      await axios.put(`/api/admin/email-topup-tiers/${id}`, {
        name: tier.name,
        emails: tier.emails,
        priceRands: tier.priceRands,
        sortOrder: tier.sortOrder,
        isActive: tier.isActive
      });
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving tier');
    } finally {
      setBusy(false);
    }
  };

  const createTier = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post('/api/admin/email-topup-tiers', newTier);
      setNewTier(emptyTier);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error creating tier');
    } finally {
      setBusy(false);
    }
  };

  const deactivateTier = async (tier) => {
    if (!window.confirm(`Stop offering "${tier.name}"? Past purchases are kept.`)) return;
    setBusy(true);
    try {
      await axios.delete(`/api/admin/email-topup-tiers/${tier.id}`);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error deactivating tier');
    } finally {
      setBusy(false);
    }
  };

  const rands = (cents) => `R${(cents / 100).toFixed(2)}`;
  const perEmail = (tier) => {
    const priceRands = Number(tier.priceRands);
    const emails = Number(tier.emails);
    if (!priceRands || !emails) return '-';
    return `R${(priceRands / emails).toFixed(3)} each`;
  };

  if (isLoading) return <div className="loading">Loading top-up tiers...</div>;

  return (
    <div className="email-topup-tiers">
      <h2>Email Top-Up Tiers</h2>
      <p>
        What vendors can buy when they run out of monthly emails. Extra emails
        apply to the current month only. Prices include VAT.
      </p>
      {error && <div className="search-error">{error}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Emails</th>
              <th>Price (R)</th>
              <th>Per email</th>
              <th>Order</th>
              <th>Active</th>
              <th>Sold</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map(tier => {
              const draft = editing[tier.id] || {};
              return (
                <tr key={tier.id}>
                  <td>
                    <input
                      value={draft.name || ''}
                      onChange={(e) => updateField(tier.id, 'name', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      value={draft.emails || ''}
                      onChange={(e) => updateField(tier.id, 'emails', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={draft.priceRands || ''}
                      onChange={(e) => updateField(tier.id, 'priceRands', e.target.value)}
                    />
                  </td>
                  <td>{perEmail(draft)}</td>
                  <td>
                    <input
                      type="number"
                      value={draft.sortOrder || '0'}
                      onChange={(e) => updateField(tier.id, 'sortOrder', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!draft.isActive}
                      onChange={(e) => updateField(tier.id, 'isActive', e.target.checked)}
                    />
                  </td>
                  <td>{tier.purchases}</td>
                  <td>
                    <button type="button" onClick={() => saveTier(tier.id)} disabled={busy}>
                      Save
                    </button>
                    {tier.is_active && (
                      <button
                        type="button"
                        className="remove-test-btn"
                        onClick={() => deactivateTier(tier)}
                        disabled={busy}
                      >
                        Retire
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3>Add a tier</h3>
      <form onSubmit={createTier} className="search-section">
        <div className="search-input-group">
          <input
            placeholder="Name"
            value={newTier.name}
            onChange={(e) => setNewTier({ ...newTier, name: e.target.value })}
          />
          <input
            type="number"
            min="1"
            placeholder="Emails"
            value={newTier.emails}
            onChange={(e) => setNewTier({ ...newTier, emails: e.target.value })}
          />
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Price in Rand"
            value={newTier.priceRands}
            onChange={(e) => setNewTier({ ...newTier, priceRands: e.target.value })}
          />
          <input
            type="number"
            placeholder="Sort order"
            value={newTier.sortOrder}
            onChange={(e) => setNewTier({ ...newTier, sortOrder: e.target.value })}
          />
          <button type="submit" disabled={busy}>Add Tier</button>
        </div>
      </form>

      <h3>Recent Purchases</h3>
      {purchases.length === 0 ? (
        <p>No top-up purchases yet.</p>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Business</th>
                <th>Tier</th>
                <th>Emails</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Started</th>
                <th>Credited</th>
                <th>PayFast ref</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map(p => (
                <tr key={p.id}>
                  <td>{p.business_name}</td>
                  <td>{p.tier_name || '-'}</td>
                  <td>{p.emails}</td>
                  <td>{rands(p.amount_cents)}</td>
                  <td>{p.status}</td>
                  <td>{new Date(p.created_at).toLocaleString()}</td>
                  <td>{p.credited_at ? new Date(p.credited_at).toLocaleString() : '-'}</td>
                  <td>{p.pf_payment_id || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default EmailTopupTiers;
